import { useEffect, useState } from "react";
import { X } from "lucide-react";
import * as usersApi from "../api/users";
import { ClienteLite } from "../api/users";

/**
 * Selettore multiplo di clienti dell'anagrafica (Fase 3.3), usato sia
 * nell'invito di un utente cliente sia nella gestione successiva delle aziende
 * collegate. Ricerca in typeahead (min 2 caratteri) su GET /api/clienti-search
 * e mostra le selezioni come tag rimovibili. Lo stato è controllato dal padre.
 */
export function ClientiMultiSelect({
  selected,
  onChange,
}: {
  selected: ClienteLite[];
  onChange: (clienti: ClienteLite[]) => void;
}) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState<ClienteLite[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (q.trim().length < 2) {
      setResults([]);
      return;
    }
    let active = true;
    setLoading(true);
    const timer = setTimeout(() => {
      usersApi
        .searchClienti(q)
        .then((r) => {
          if (active) setResults(r);
        })
        .catch(() => {
          if (active) setResults([]);
        })
        .finally(() => {
          if (active) setLoading(false);
        });
    }, 250);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [q]);

  const selectedIds = new Set(selected.map((s) => s.id));
  const suggestions = results.filter((r) => !selectedIds.has(r.id));

  function add(c: ClienteLite) {
    if (!selectedIds.has(c.id)) onChange([...selected, c]);
    setQ("");
    setResults([]);
    setOpen(false);
  }

  function remove(id: string) {
    onChange(selected.filter((s) => s.id !== id));
  }

  return (
    <div className="flex flex-col gap-2">
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((c) => (
            <span
              key={c.id}
              className="inline-flex items-center gap-1 rounded-md border border-line bg-ink/[0.03] px-2 py-1 text-xs text-ink"
            >
              {c.nome}
              {c.insegna ? ` · ${c.insegna}` : ""}
              <button
                type="button"
                onClick={() => remove(c.id)}
                aria-label={`Rimuovi ${c.nome}`}
                className="text-ink/40 transition hover:text-danger"
              >
                <X className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="relative">
        <input
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder="Cerca un cliente per nome o insegna…"
          className="h-10 w-full rounded-lg border border-line bg-white px-3 text-sm text-ink"
        />
        {open && q.trim().length >= 2 && (
          <div className="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-lg border border-line bg-white shadow-lift">
            {loading ? (
              <p className="px-3 py-2 text-sm text-ink/40">Ricerca…</p>
            ) : suggestions.length === 0 ? (
              <p className="px-3 py-2 text-sm text-ink/40">Nessun cliente trovato.</p>
            ) : (
              suggestions.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()} // evita il blur prima del click
                  onClick={() => add(c)}
                  className="flex w-full flex-col items-start px-3 py-2 text-left text-sm transition hover:bg-ink/[0.03]"
                >
                  <span className="text-ink">{c.nome}</span>
                  {c.insegna && <span className="text-xs text-ink/50">{c.insegna}</span>}
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}
